import Axios from '@/utils/axios';
import { getBackendErrorMessage } from '@/utils/errorHandler';
import { Pagination } from './deliveries';
import {
  PackageCondition,
  PackageMedia,
  PackageRow,
  PackageStatus,
} from './packages';

// ─── Shipments API ────────────────────────────────────────────────────
// GET /v1/shipments — paginated list of the client's inbound shipments
// (one waybill per row, grouped under an upload-shipment batch).
// Same LINK channel + live-mode headers as the other client endpoints; the
// bearer token is attached by the shared axios interceptor.
const CHANNEL_HEADERS_LIVE = {
  'X-Channel': 'LINK',
  'X-Api-Mode': 'live',
} as const;

// The ten shipment statuses of Status Model v2 (docs/09-FE-CHANGES.md §1), in
// lifecycle order. No name moved here — AT_HUB / RETURNED / FAILED are new.
// The badge still falls back to a title-cased label for anything unmapped.
//
// Shipment status is NOT monotonic: `IN_DELIVERY → AT_HUB` (rider failed, goods
// rescanned at the hub), `FAILED → AT_HUB` (a lost item turned up) and
// `COMPLETED → AT_HUB` (new items appended to the AWB) are all legal (§5).
export const SHIPMENT_STATUSES = [
  'CREATED',
  // First inbound scan of any item on the waybill flips CREATED → AT_HUB, so
  // this is a high-traffic status, not an edge case.
  'AT_HUB',
  'PARTIALLY_DISPATCHED',
  'DISPATCHED',
  'IN_DELIVERY',
  // Retur H0 — a package is being returned on the spot at delivery. Surfaced
  // as "In Dispute" in the UI. Recognised by the gateway status filter.
  'IN_DISPUTE',
  'COMPLETED',
  // Not a single item on the waybill was delivered.
  'RETURNED',
  // Every active item is spent and at least one is LOST.
  'FAILED',
  'CANCELLED',
] as const;

export type ShipmentStatus = (typeof SHIPMENT_STATUSES)[number];

export interface ShipmentClient {
  id: number;
  name: string;
}

/** EXIM direction tag on a 4W shipment. GENERAL = non-EXIM / umum. */
export type ShipmentExim = 'EXPORT' | 'IMPORT' | 'GENERAL';

/**
 * One frozen additional note captured at 4W intake (e.g. BL / FO / AGL codes).
 * Stored self-describing: `label` is what ops typed, `value` the entry.
 */
export interface ShipmentAdditionalNote {
  label: string;
  value: string;
}

/** Official-document slots on a 4W shipment (direction-specific set). */
export type ShipmentDocumentType =
  | 'surat_jalan'
  | 'surat_dn'
  | 'dokumen_timbang_akhir'
  | 'eir_delivery'
  | 'eir_receiving'
  | 'bl';

/**
 * One ops-attested official document (PDF) committed onto the shipment.
 * Patimban slots are keyed by type; other clients' documents are keyed
 * `doc_<name>` and carry the name ops typed in `label`.
 */
export interface ShipmentOfficialDocument {
  label?: string;
  url: string;
  filename: string;
  contentType: string | null;
  uploadedBy: string;
  uploadedAt: string;
}

// A single row in the shipments list response.
export interface Shipment {
  id: string;
  waybill: string;
  uploadShipmentId?: string;
  uploadShipmentCode?: string;
  clientId?: number;
  client?: ShipmentClient | null;
  bookingId?: string | null;
  deliveryGroupId?: string | null;
  serviceType?: string | null;
  senderName?: string | null;
  senderPhone?: string | null;
  receiverName?: string | null;
  receiverPhone?: string | null;
  originAddress?: string | null;
  originLat?: number | null;
  originLong?: number | null;
  originPhone?: string | null;
  destinationAddress?: string | null;
  destinationLat?: number | null;
  destinationLong?: number | null;
  destinationPhone?: string | null;
  totalWeight?: number | null;
  totalVolume?: number | null;
  status: ShipmentStatus | string;
  externalProviderId?: number | null;
  externalShiftId?: number | null;
  revenueStream?: string | null;
  hubId?: number | null;
  dispatchedAt?: string | null;
  cancelledAt?: string | null;
  cancellationReason?: string | null;
  createdAt?: string;
  updatedAt?: string;
  itemCount?: number;
  // Sum of `items[].koli` for this shipment (koli-trd-v1 §8.5). Present on both
  // the list rows and the detail payload; invariant totalKoli >= itemCount.
  totalKoli?: number;
  /** EXIM direction tag (4W). Absent/GENERAL for non-EXIM shipments. */
  exim?: ShipmentExim | null;
  /**
   * Frozen recon notes from 4W intake, keyed by note key (bl_number, fo_number,
   * agl_route_code, …). Present on both list rows and the detail payload.
   */
  additionalNotes?: Record<string, ShipmentAdditionalNote> | null;
}

// The envelope is { status, data: { data: [...], pagination } } — note the
// double-nested `data` (unlike deliveries, which is flat under `data`).
export interface ShipmentListResponse {
  status: string;
  data: {
    data: Shipment[];
    pagination: Pagination;
  };
}

export interface ShipmentListParams {
  page?: number;
  limit?: number;
  hubId?: string | number;
  uploadShipmentCode?: string;
  clientId?: string | number;
  createdFrom?: string; // YYYY-MM-DD
  createdTo?: string; // YYYY-MM-DD
  search?: string; // waybill / booking ID
  status?: string; // comma-separated ShipmentStatus values
  /** EXIM direction tag filter (EXPORT | IMPORT | GENERAL). */
  exim?: ShipmentExim;
}

export const getShipments = async (
  params: ShipmentListParams = {}
): Promise<{ data: Shipment[]; pagination: Pagination }> => {
  try {
    // Send only page/limit by default; optional filters are omitted entirely
    // unless they carry a real value.
    const query: Record<string, string | number> = {
      page: params.page ?? 1,
      limit: params.limit ?? 20,
    };
    if (params.hubId) query.hubId = params.hubId;
    if (params.uploadShipmentCode)
      query.uploadShipmentCode = params.uploadShipmentCode;
    if (params.clientId) query.clientId = params.clientId;
    if (params.createdFrom) query.createdFrom = params.createdFrom;
    if (params.createdTo) query.createdTo = params.createdTo;
    if (params.search) query.search = params.search;
    if (params.status) query.status = params.status;

    const response = await Axios.get<ShipmentListResponse>('/v1/shipments', {
      headers: CHANNEL_HEADERS_LIVE,
      params: query,
    });
    return {
      data: response.data.data?.data ?? [],
      pagination: response.data.data?.pagination ?? {
        total: 0,
        page: 1,
        size: params.limit ?? 20,
        lastPage: 1,
      },
    };
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

// ─── Summary (RINGKASAN cards) ────────────────────────────────────────
// GET /v1/shipments/summary — per-status counts + total for the current
// client/search/date scope (same filters as the list minus status/pagination).
// The BE returns every status; the page picks which cards to show.
// Flat count-per-status + total (mirrors InvoiceSummary). Fields optional so a
// partial/failed payload still renders (cards fall back to 0).
// One key per ShipmentStatus (Status Model v2 §2 — `atHub`, `returned` and
// `failed` are the additions). `total` is the sum of all ten, so a card row that
// hides any of them won't add up.
export interface ShipmentSummary {
  total?: number;
  created?: number;
  atHub?: number;
  partiallyDispatched?: number;
  dispatched?: number;
  inDelivery?: number;
  inDispute?: number;
  completed?: number;
  returned?: number;
  failed?: number;
  cancelled?: number;
}

interface ShipmentSummaryResponse {
  status: string;
  data: ShipmentSummary;
}

export type ShipmentSummaryParams = Omit<
  ShipmentListParams,
  'page' | 'limit' | 'status'
>;

export const getShipmentSummary = async (
  params: ShipmentSummaryParams = {}
): Promise<ShipmentSummary> => {
  try {
    const query: Record<string, string | number> = {};
    if (params.hubId) query.hubId = params.hubId;
    if (params.uploadShipmentCode)
      query.uploadShipmentCode = params.uploadShipmentCode;
    if (params.clientId) query.clientId = params.clientId;
    if (params.createdFrom) query.createdFrom = params.createdFrom;
    if (params.createdTo) query.createdTo = params.createdTo;
    if (params.search) query.search = params.search;

    const response = await Axios.get<ShipmentSummaryResponse>(
      '/v1/shipments/summary',
      { headers: CHANNEL_HEADERS_LIVE, params: query }
    );
    return response.data.data ?? {};
  } catch {
    // Summary is non-critical — leave cards at 0 if it fails (mirrors
    // getInvoiceSummary). Never throw, so the page/table stays usable.
    return {};
  }
};

// ─── Detail ───────────────────────────────────────────────────────────
// One delivery leg of an item (Status Model v2 §4). An item can be attempted
// several times: each forward attempt is a `LAST_MILE` leg, the trip back to the
// hub is a `RETURN_TO_HUB` leg. `legs[]` replaces the old single
// `externalDeliveryUid` + `de2DeliveryUid` pair.
export type ItemLegType = 'LAST_MILE' | 'RETURN_TO_HUB';

export interface ItemDeliveryLeg {
  id?: string;
  // 1-based ordering of the legs for this item.
  legSeq?: number;
  legType?: ItemLegType | string;
  // The delivery (DE-xx) this leg was carried on.
  deliveryUid?: string | null;
  // DeliveryStatus from the delivery-service (unchanged by v2), e.g.
  // 'COMPLETED' | 'FAILED'.
  deliveryStatus?: string | null;
  // Exactly one leg is the item's current one; older legs are closed.
  isActive?: boolean;
  createdAt?: string;
}

// A single item/parcel inside a shipment. Field names are kept loose where
// the gateway is inconsistent; the UI falls back gracefully on missing values.
export interface ShipmentItem {
  id?: string;
  shipmentId?: string;
  itemName?: string;
  invoice?: string;
  description?: string;
  quantity?: number;
  // Jumlah koli (physical parcels) for this item — distinct from `quantity`
  // (unit count, always 1 for imported items). BE default is 1.
  koli?: number;
  weight?: number;
  volume?: number;
  price?: number | null;
  status?: string; // per-item handling status (falls back to parent status)
  // Physical condition — independent of `status` (Status Model v2 §4). A damaged
  // item keeps moving to COMPLETED, so render it as a badge, not a status.
  condition?: PackageCondition | null;
  // Retur details, captured when the item is sent back (previously write-only).
  returReasonCode?: string | null;
  returReasonNote?: string | null;
  returWeight?: number | null;
  // Every delivery attempt + the retur trip, oldest first. See the leg helpers
  // below rather than reading this directly.
  legs?: ItemDeliveryLeg[];
  scannedAt?: string | null;
  assignedRiderCode?: string | null;
  /**
   * @deprecated Pointer to the **forward** leg only, kept for pre-`legs[]`
   * readers and slated for removal. It is no longer repointed at the retur
   * delivery, so it stays on DE-1 even while the item is being returned — use
   * `activeDeliveryUid()` / `returnDeliveryUid()` instead. (`de2DeliveryUid` is
   * gone entirely — the column was dropped, so it reads `undefined`, not null.)
   */
  externalDeliveryUid?: string | null;
  cancelledAt?: string | null;
  cancellationReason?: string | null;
  originAddress?: string | null;
  destinationAddress?: string | null;
  createdAt?: string;
  updatedAt?: string;
  // POD photos for this item (POD-per-package), oldest first. Same shape as the
  // packages menu — empty for non-POD clients or before delivery completion.
  media?: PackageMedia[];
}

// The detail payload is the full shipment plus its item list.
/**
 * One captured field on a timeline event, frozen by BE at capture time with the
 * field type that produced it — so the UI never resolves the workflow
 * definition to know whether this is a photo, text, or a number.
 *
 * `value` is ALWAYS an array: IMAGE is natively multi-URL, SIGNATURE is exactly
 * one PNG URL, and TEXT/NUMBER are boxed so there is one shape to render
 * instead of two.
 */
export interface ShipmentTimelineAttachment {
  /** Workflow field key, e.g. `ktpImage`. */
  key: string;
  /** Field label as the driver saw it. */
  name: string;
  /**
   * IMAGE | TEXT | NUMBER | SIGNATURE. Kept a plain string (the gateway may add
   * types before Link knows them), so the compiler won't catch an unhandled
   * one — the drawer lists its file types explicitly.
   */
  type: string;
  value: Array<string | number>;
}

/**
 * Who filled a stop-workflow milestone: the rider on the app, or ops on the
 * rider's behalf (the force-terminal override, where a signature field holds a
 * photo of the signed delivery note instead of a drawn signature). `null` until
 * the milestone is submitted. Gateways that predate it omit the field — treat
 * missing exactly like `null`: no source badge.
 */
export type WorkflowSubmittedVia = 'RIDER' | 'OPS';

/**
 * One event on the shipment timeline. `STATUS_CHANGE` mirrors the status
 * history (`fromStatus` → `status`); `WORKFLOW_MILESTONE` is a door milestone
 * the driver submitted, which does NOT move the status — on those, `status` is
 * where the shipment stood at the time and `fromStatus` is null.
 */
export interface ShipmentTimelineEvent {
  id: string;
  /** STATUS_CHANGE | WORKFLOW_MILESTONE | NOTE. */
  eventType: string;
  /** Shipment status at the moment of the event — set on every event. */
  status: string;
  /** Only on STATUS_CHANGE; null when the event moved nothing. */
  fromStatus: string | null;
  /** Milestone name on a door event; null on a plain status change. */
  title: string | null;
  note: string | null;
  routeStopId: string | null;
  workflowId: string | null;
  workflowSubmissionId: string | null;
  milestoneKey: string | null;
  milestoneSequence: number | null;
  /** Captured fields (photos, text, numbers); `[]` when there are none. */
  attachments: ShipmentTimelineAttachment[];
  /** Milestone rows only; see `WorkflowSubmittedVia`. Absent on older builds. */
  submittedVia?: WorkflowSubmittedVia | null;
  /**
   * Internal actor (ops email / `rider:<id>` / `system`). NOT rendered in Link
   * — this is the client-facing portal and internal identities do not belong
   * in it.
   */
  createdBy: string | null;
  occurredAt: string;
}

// ─── 4W route / driver evidence ───────────────────────────────────────
// Driver-captured evidence for a 4W/EXIM shipment's route: per-stop milestone
// photos plus the start/end vehicle-condition checks. Detail endpoint only, and
// only for shipments that ride a route; absent/null otherwise. These power the
// "Cetak foto" Dokumen Lampiran export (see EvidencePrintModal).

/** One IMAGE or SIGNATURE field of a milestone and its captured file URLs. */
export interface ShipmentRoutePhotoField {
  fieldKey: string;
  label: string;
  /**
   * Field type. A SIGNATURE holds exactly one PNG URL. Absent on older gateway
   * builds and on vehicle-check fields — read as IMAGE.
   */
  type?: 'IMAGE' | 'SIGNATURE';
  /** Empty until the milestone is submitted. */
  urls: string[];
}

/** A milestone that carries at least one photo or signature field. */
export interface ShipmentRouteMilestone {
  key: string;
  name: string;
  /** PENDING | COMPLETED. */
  status: string;
  submittedAt: string | null;
  photoFields: ShipmentRoutePhotoField[];
  /** See `WorkflowSubmittedVia`. Absent on older gateway builds. */
  submittedVia?: WorkflowSubmittedVia | null;
}

export interface ShipmentRouteStop {
  id: string;
  sequence: number;
  /** PICKUP | DROP_OFF. */
  type: string;
  address: string;
  /** PENDING | COMPLETED | FAILED | SKIPPED. */
  status: string;
  arrivedAt: string | null;
  resolvedAt: string | null;
  milestones: ShipmentRouteMilestone[];
}

/**
 * A DIRECT_4W vehicle-condition check (start-of-trip or end-of-trip): the fixed
 * condition photos as labelled fields (same shape as milestone photos) plus the
 * odometer reading. `null` for non-4W routes / phases not yet submitted.
 */
export interface ShipmentRouteVehicleCheck {
  /** Selfie, four sides, box, odometer — labelled, in capture order. */
  photoFields: ShipmentRoutePhotoField[];
  odometerReading: number;
  submittedAt: string | null;
}

/** Truck as booked on the route — the Dokumen Lampiran "Kendaraan" row. */
export interface ShipmentRouteVehicle {
  plateNumber: string | null;
  unitType: string | null;
}

export interface ShipmentRoute {
  routeId: string;
  code: string;
  /** PLANNED | IN_PROGRESS | COMPLETED. */
  status: string;
  startedAt: string | null;
  completedAt: string | null;
  /** Frozen truck snapshot; null on routes created before it was captured. */
  vehicle: ShipmentRouteVehicle | null;
  /** Rider name off the route's snapshot — the document header needs it. */
  driverName: string | null;
  /** Rider code off the route's snapshot (driver's roster code). */
  driverCode: string | null;
  /** Vehicle condition at trip start (DIRECT_4W); null otherwise / until submitted. */
  startCheck: ShipmentRouteVehicleCheck | null;
  /** Vehicle condition at trip end (DIRECT_4W); null otherwise / until submitted. */
  endCheck: ShipmentRouteVehicleCheck | null;
  stops: ShipmentRouteStop[];
}

/**
 * One end of a DIRECT_4W trip: the stop it happened at, the milestone photos
 * captured there, and that phase's vehicle-condition check. Every stop field
 * is nullable — a route may carry a start check before any stop resolves, or
 * serve a shipment that only has a drop-off.
 */
export interface Direct4WSideEvidence {
  stopId: string | null;
  sequence: number | null;
  /** PICKUP | DROP_OFF. */
  type: string | null;
  address: string | null;
  /** PENDING | COMPLETED | FAILED | SKIPPED. */
  status: string | null;
  arrivedAt: string | null;
  resolvedAt: string | null;
  /** Only milestones carrying at least one photo field; `[]` when none. */
  milestones: ShipmentRouteMilestone[];
  /** `startCheck` on the start side, `endCheck` on the end side. */
  vehicleCheck: ShipmentRouteVehicleCheck | null;
}

/**
 * The start/end evidence pair for a DIRECT_4W shipment — deliberately separate
 * from `route` (the full stop list) and from `timeline`. `null` for every other
 * shipment type and for a 4W route that has captured nothing yet.
 */
export interface Direct4WEvidence {
  routeId: string;
  routeCode: string;
  start: Direct4WSideEvidence | null;
  end: Direct4WSideEvidence | null;
}

export interface ShipmentDetail extends Shipment {
  items?: ShipmentItem[];
  /**
   * Status transitions + driver milestones in one stream, **newest first**.
   * Absent on older gateway builds.
   */
  timeline?: ShipmentTimelineEvent[];
  /** How the consignment is carried: DOCKING | DIRECT_2W | DIRECT_4W. */
  type?: string;
  /** DIRECT_4W cargo tag (FCL_40FT / NON_CONTAINER / …); null otherwise. */
  containerType?: string | null;
  /**
   * Driver-captured milestone photos for a 4W route. Detail endpoint only, and
   * only for shipments that ride a route (4W/EXIM); `null`/absent otherwise.
   */
  route?: ShipmentRoute | null;
  /**
   * DIRECT_4W only: the trip's start/end evidence pair. Detail endpoint only;
   * `null` for other shipment types and on older gateway builds.
   */
  direct4wEvidence?: Direct4WEvidence | null;
  /**
   * DIRECT_4W: ops-attested official documents (PDF) keyed by document type
   * (surat_jalan, bl, eir_delivery, …) or, outside Patimban, by `doc_<name>`.
   * `null`/absent when none uploaded.
   */
  officialDocuments?: Record<string, ShipmentOfficialDocument> | null;
}

export interface ShipmentDetailResponse {
  status: string;
  data: ShipmentDetail;
}

// ─── Leg helpers (Status Model v2 §4) ─────────────────────────────────
// Everything the UI used to read off `externalDeliveryUid` / `de2DeliveryUid`
// now comes from `legs[]`. Kept here (not in a component) so the shipments page
// and the packages page answer these questions the same way.

// Legs oldest-first. The BE already orders them; sort defensively so `legSeq`
// stays the single source of truth for the display order.
export const itemLegs = (item: ShipmentItem): ItemDeliveryLeg[] =>
  [...(item.legs ?? [])].sort((a, b) => (a.legSeq ?? 0) - (b.legSeq ?? 0));

// Forward delivery attempts only (excludes the trip back to the hub).
export const lastMileLegs = (item: ShipmentItem): ItemDeliveryLeg[] =>
  itemLegs(item).filter((leg) => leg.legType === 'LAST_MILE');

// How many times we tried to deliver this item.
export const deliveryAttemptCount = (item: ShipmentItem): number =>
  lastMileLegs(item).length;

// The forward delivery the item is on now: the active LAST_MILE leg, else the
// most recent one. Falls back to the deprecated `externalDeliveryUid` so
// payloads that predate `legs[]` still render.
export const activeDeliveryUid = (item: ShipmentItem): string | null => {
  const legs = lastMileLegs(item);
  const active = [...legs].reverse().find((leg) => leg.isActive);
  const leg = active ?? legs[legs.length - 1];
  return leg?.deliveryUid ?? item.externalDeliveryUid ?? null;
};

// The retur leg (what `de2DeliveryUid` used to point at), or null.
export const returnLeg = (item: ShipmentItem): ItemDeliveryLeg | null =>
  itemLegs(item).find((leg) => leg.legType === 'RETURN_TO_HUB') ?? null;

export const returnDeliveryUid = (item: ShipmentItem): string | null =>
  returnLeg(item)?.deliveryUid ?? null;

// "Failed but not back yet": a leg came back FAILED while the item itself is
// still IN_DELIVERY — physically with the rider, no retur leg opened.
export const isFailedAwaitingReturn = (item: ShipmentItem): boolean =>
  item.status === 'IN_DELIVERY' &&
  itemLegs(item).some((leg) => leg.deliveryStatus === 'FAILED');

// GET /v1/shipments/{id} — full shipment detail.
export const getShipmentDetail = async (
  id: string
): Promise<ShipmentDetail> => {
  try {
    const response = await Axios.get<ShipmentDetailResponse>(
      `/v1/shipments/${id}`,
      { headers: CHANNEL_HEADERS_LIVE }
    );
    return response.data.data;
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

// ─── Cancel ───────────────────────────────────────────────────────────
// POST /v1/shipments/{id}/cancel — soft-cancel a whole shipment: the backend
// flips it (and all its items) to CANCELLED and recomputes totals. Only CREATED
// shipments are cancellable; the server rejects anything further along. `reason`
// is optional and stored as the cancellation note. `cancelledBy`: actor
// attribution — provider-user tokens carry no email, so the BE records this
// instead of "system" (same rule as import:commit's uploadedBy).
export const cancelShipment = async (
  id: string,
  { cancelledBy, reason }: { cancelledBy?: string; reason?: string } = {}
): Promise<ShipmentDetail> => {
  try {
    const response = await Axios.post<ShipmentDetailResponse>(
      `/v1/shipments/${id}/cancel`,
      {
        reason: reason || undefined,
        cancelledBy: cancelledBy || undefined,
      },
      { headers: CHANNEL_HEADERS_LIVE }
    );
    return response.data.data;
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

// A shipment can only be cancelled while still CREATED (matches the BE
// CancelShipmentUseCase guard). Exposed so the list + detail drawer gate the
// action the same way.
export const isShipmentCancellable = (
  status: ShipmentStatus | string
): boolean => status === 'CREATED';

// ─── Bulk items (multi-shipment label print) ─────────────────────────
interface BulkShipmentItemsResponse {
  status: string;
  data: ShipmentDetail[];
}

// POST /v1/shipments/items:bulk — fetch several shipments + their items in ONE
// round-trip (replaces N getShipmentDetail calls). The caller builds the label
// rows with `details.flatMap(shipmentToPackageRows)`.
export const getBulkShipmentItems = async (
  shipmentIds: string[]
): Promise<ShipmentDetail[]> => {
  try {
    const response = await Axios.post<BulkShipmentItemsResponse>(
      '/v1/shipments/items:bulk',
      { shipmentIds },
      { headers: CHANNEL_HEADERS_LIVE }
    );
    return response.data.data ?? [];
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

// Map a shipment + its items into the PackageRow shape the label printer
// (LabelPreviewModal) consumes — one row per item. Because we have the WHOLE
// shipment's items here, itemSeq/itemTotal are numbered locally and are the
// true totals (no backend dependency on this page). Addresses fall back from
// the item to the parent shipment.
//
// Cancelled items are excluded entirely: no label is printed for them, and they
// don't count toward the X/Y position — matching the packages-page behaviour.
//
// `fallbackClient` (the authenticated company) backs the label header when the
// shipment payload omits its own client snapshot — Link is single-client, so
// the shipment's owner is always the logged-in client.
export const shipmentToPackageRows = (
  detail: ShipmentDetail,
  fallbackClient?: ShipmentClient | null
): PackageRow[] => {
  const items = (detail.items ?? []).filter(
    (item) => item.status !== 'CANCELLED'
  );
  return items.map((item, i) => ({
    id: item.id ?? `${detail.id}:${i}`,
    po: item.invoice ?? '',
    deliveryId: detail.waybill,
    bookingId: detail.bookingId ?? undefined,
    outlet: detail.receiverName ?? '',
    sender: detail.senderName ?? '',
    client: detail.client ?? fallbackClient ?? undefined,
    qty: item.quantity ?? 0,
    koli: item.koli,
    weight: item.weight ?? 0,
    volume: item.volume ?? 0,
    status: (item.status as PackageStatus) ?? 'CREATED',
    recipientAddress:
      item.destinationAddress ?? detail.destinationAddress ?? undefined,
    senderAddress: item.originAddress ?? detail.originAddress ?? undefined,
    itemName: item.itemName ?? undefined,
    itemSeq: i + 1,
    itemTotal: items.length,
  }));
};

// ─── Import CSV (bulk upload) ─────────────────────────────────────────────────

// Client (Dash Hub) upload format. Only Receiver Name + Item Name are truly
// required; the rest is optional or backfilled by the backend — origin from the
// selected hub, destination coords/address from client masterdata (matched on
// Receiver Name), Service Type defaults to INSTANT. Multiple items for one row
// go in the Item Name cell separated by newlines.
// ── Import template ───────────────────────────────────────────────────
// The CSV template is served by logistic-service
// (GET /v1/shipments/import/template), not generated here. It used to be
// hardcoded in this file, which meant the columns could drift from what the
// import parser actually accepts, and the per-client "dedicated" split lived
// in a Firebase Remote Config flag the BE knew nothing about.
//
// The BE now owns every variant and picks between them:
//   - general        — the canonical DASH_HUB column set
//   - client-pitstop — CLIENT_PITSTOP: adds Priority Time Window; the origin
//                      cells ship blank (blank = taken from the picked pitstop)
//   - dropoff        — anchorRole=DESTINATION ("Titik Dropoff"): origin
//                      columns required per row, destination columns optional
//                      (blank = the picked hub/pitstop); wins over the others
//   - dedicated      — the lean sheet, for clients listed in the
//                      DEDICATED_IMPORT_SHIPMENTS_TEMPLATE_CLIENT_IDS env
//
// `clientId` selects the dedicated variant. A provider-user token already
// carries its own client, and the BE prefers that over this param, so sending
// it is belt-and-braces — it keeps the request self-describing and works for
// any caller whose token has no client scope.
export const downloadShipmentTemplate = async (params: {
  originMode: ImportOriginMode;
  /** Omitted = ORIGIN (legacy). DESTINATION serves the dropoff sheet. */
  anchorRole?: ImportAnchorRole;
  clientId?: number | null;
}): Promise<void> => {
  try {
    const response = await Axios.get('/v1/shipments/import/template', {
      headers: CHANNEL_HEADERS_LIVE,
      params: {
        originMode: params.originMode,
        ...(params.anchorRole ? { anchorRole: params.anchorRole } : {}),
        ...(params.clientId != null ? { clientId: params.clientId } : {}),
      },
      responseType: 'blob',
    });
    // Trust the BE's filename so the variant it chose is visible on disk.
    const disposition = response.headers['content-disposition'] as
      | string
      | undefined;
    const filename =
      disposition?.match(/filename="?([^"]+)"?/i)?.[1] ||
      'shipment-import-template.csv';
    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.setAttribute('aria-label', 'Download shipment CSV template');
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

export interface ImportPreviewItemRow {
  rowNumber: number;
  itemName: string;
  weight: number;
  // Koli for this item (1 when the CSV `Koli` cell is blank/absent).
  koli?: number;
}

export interface ImportPreviewShipmentRow {
  bookingId: string;
  receiverName: string;
  serviceType: string;
  destinationAddress: string;
  itemCount: number;
  totalWeight: number;
  // Sum koli over ALL of this AWB's items — the `items` array below is capped
  // by the BE (PREVIEW_LIST_CAP), so display this, never reduce over `items`
  // (koli-trd-v1 §8.2).
  totalKoli?: number;
  items: ImportPreviewItemRow[];
}

export interface ImportPreviewDuplicateRow {
  bookingId: string;
  existingShipmentId: string;
  existingWaybill: string;
  existingStatus: string;
  appendItemCount: number;
  skippedItemCount: number;
}

export interface ImportPreviewError {
  rowNumber: number;
  field: string;
  value: string;
  message: string;
}

export interface ImportPreviewSummary {
  totalRows: number;
  toCreateShipments: number;
  toCreateItems: number;
  toAppendShipments: number;
  toAppendItems: number;
  skippedDuplicateItems: number;
  duplicates: number;
  errors: number;
  ignoredColumns: string[];
  /** Echo of the resolved origin mode (older BE builds omit it). */
  originMode?: ImportOriginMode;
  /** Echo of the anchor role (ORIGIN when omitted / older BE builds). */
  anchorRole?: ImportAnchorRole;
  /** The hub / client pitstop the upload is anchored to; null for DIRECT. */
  origin?: ImportPreviewOrigin | null;
}

/** Echo of the anchor hub / pitstop on the dry-run summary. */
export interface ImportPreviewOrigin {
  pitstopId: number;
  name: string;
  address: string;
  pitstopType: string;
}

export interface ImportPreviewResult {
  summary: ImportPreviewSummary;
  toCreate: ImportPreviewShipmentRow[];
  duplicates: ImportPreviewDuplicateRow[];
  errors: ImportPreviewError[];
}

export interface ImportCommitResult {
  uploadShipmentId: string;
  createdShipments: number;
  appendedItems: number;
  skippedDuplicateItems: number;
}

/**
 * Where the goods start (import TRD §D3).
 *   - `DASH_HUB` — client delivers to a Dash hub, riders depart from there.
 *   - `CLIENT_PITSTOP` — goods wait at the client's own pitstop
 *     (`PROVIDER_WAREHOUSE`) and a rider collects them there; no hub scan.
 *   - `DIRECT` — not offered yet ("Segera hadir").
 */
export type ImportOriginMode = 'DASH_HUB' | 'CLIENT_PITSTOP' | 'DIRECT';

/**
 * Which side the hub/pitstop anchor plays (UI: "dipakai sebagai Titik Pickup /
 * Titik Dropoff"). ORIGIN = legacy (goods depart FROM the anchor);
 * DESTINATION = retrieval (goods picked up at the CSV addresses, dropped off
 * AT the anchor). The anchor only fills blank cells on its side — a row that
 * carries its own origin/destination keeps it. Meaningless for DIRECT.
 */
export type ImportAnchorRole = 'ORIGIN' | 'DESTINATION';

export type ImportErrorCode =
  | 'IMPORT_FILE_REQUIRED'
  | 'IMPORT_FILE_TOO_LARGE'
  | 'IMPORT_FILE_INVALID'
  | 'IMPORT_TOO_MANY_ROWS'
  | 'IMPORT_TOO_MANY_ERRORS'
  | 'IMPORT_VALIDATION_FAILED'
  | 'IMPORT_ANCHOR_ROLE_INVALID'
  // CLIENT_PITSTOP gates: the pitstop must be a PROVIDER_WAREHOUSE owned by
  // this client, and it must carry usable coordinates in core masterdata.
  | 'IMPORT_ORIGIN_CONFLICT'
  | 'IMPORT_PITSTOP_REQUIRED'
  | 'IMPORT_PITSTOP_MISMATCH'
  | 'UNKNOWN';

export class ShipmentImportError extends Error {
  code: ImportErrorCode;
  preview?: ImportPreviewResult;
  constructor(code: ImportErrorCode, message: string, preview?: ImportPreviewResult) {
    super(message);
    this.code = code;
    this.preview = preview;
  }
}

type ImportEnvelope<T> = { status: string | number; message?: string; data: T; error?: string };

const unwrapImport = <T>(payload: ImportEnvelope<T> | T): T =>
  payload && typeof payload === 'object' && 'data' in (payload as any)
    ? (payload as ImportEnvelope<T>).data
    : (payload as T);

const parseImportError = (error: any): ShipmentImportError => {
  const data = error?.response?.data;
  const code = (data?.error as ImportErrorCode) || 'UNKNOWN';
  const message =
    (data?.message as string) ||
    (typeof data?.error === 'string' ? data.error : null) ||
    getBackendErrorMessage(error);
  const preview =
    data && typeof data === 'object' && data.data && data.data.summary
      ? (data.data as ImportPreviewResult)
      : undefined;
  return new ShipmentImportError(code, message, preview);
};

export const importShipmentDryRun = async (params: {
  clientId: number;
  file: File;
  hubId?: number; // origin Dash Hub (pitstop id) — DASH_HUB mode only
  originMode?: ImportOriginMode;
  /** Anchor role; omitted = ORIGIN (legacy). DESTINATION = retrieval. */
  anchorRole?: ImportAnchorRole;
  /**
   * The client's own pitstop the rider collects from. Pairs with
   * `originMode: 'CLIENT_PITSTOP'` only — sending it alongside `hubId` is
   * rejected as IMPORT_ORIGIN_CONFLICT.
   */
  pitstopId?: number;
}): Promise<ImportPreviewResult> => {
  try {
    const form = new FormData();
    form.append('clientId', String(params.clientId));
    form.append('file', params.file, params.file.name);
    if (params.hubId != null) form.append('hubId', String(params.hubId));
    if (params.originMode) form.append('originMode', params.originMode);
    if (params.anchorRole) form.append('anchorRole', params.anchorRole);
    if (params.pitstopId != null)
      form.append('pitstopId', String(params.pitstopId));
    const response = await Axios.post('/v1/shipments/import:dry-run', form, {
      headers: { ...CHANNEL_HEADERS_LIVE, 'Content-Type': undefined },
    });
    return unwrapImport<ImportPreviewResult>(response.data);
  } catch (error: any) {
    throw parseImportError(error);
  }
};

export const importShipmentCommit = async (params: {
  clientId: number;
  file: File;
  hubId?: number; // origin Dash Hub (pitstop id) — DASH_HUB mode only
  originMode?: ImportOriginMode;
  /** Anchor role; omitted = ORIGIN (legacy). DESTINATION = retrieval. */
  anchorRole?: ImportAnchorRole;
  /** The client's own pitstop — CLIENT_PITSTOP mode only (see dry-run). */
  pitstopId?: number;
  // uploadedBy: actor attribution. Provider-user tokens carry no email, so the
  // BE reads this form field for them (it's ignored for admin/WEB tokens).
  uploadedBy?: string;
}): Promise<ImportCommitResult> => {
  try {
    const form = new FormData();
    form.append('clientId', String(params.clientId));
    form.append('file', params.file, params.file.name);
    if (params.hubId != null) form.append('hubId', String(params.hubId));
    if (params.originMode) form.append('originMode', params.originMode);
    if (params.anchorRole) form.append('anchorRole', params.anchorRole);
    if (params.pitstopId != null)
      form.append('pitstopId', String(params.pitstopId));
    if (params.uploadedBy) form.append('uploadedBy', params.uploadedBy);
    const response = await Axios.post('/v1/shipments/import:commit', form, {
      headers: { ...CHANNEL_HEADERS_LIVE, 'Content-Type': undefined },
    });
    return unwrapImport<ImportCommitResult>(response.data);
  } catch (error: any) {
    throw parseImportError(error);
  }
};

// ─── Manual single-shipment create ─────────────────────────────────────
// The "Create Shipment" form has no dedicated JSON endpoint: POST /v1/shipments
// is INTERNAL_SERVICE-only (H2H from the delivery-service). A manual shipment is
// instead created by synthesising a one-shipment CSV and pushing it through the
// SAME import pipeline as the CSV importer — so address resolution, zoning, AWB
// grouping and dedup are all reused for free. Origin comes from the selected
// Dash Hub (hubId, backfilled at commit); destination from the form's map
// picker (Destination Lat/Long bypass the masterdata name lookup).

// Service tiers offered by the manual form (mirrors the import Service Type
// values; the parser defaults a blank cell to INSTANT).
// RFC-4180 cell: wrap in quotes when the value has a comma, quote, or newline
// (so the multi-line Item Name and comma-containing address survive).
const toCsvCell = (value: string): string =>
  /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

export const MANUAL_SHIPMENT_SERVICE_TYPES = [
  { value: 'INSTANT', label: 'Express Now', hint: 'Langsung setelah pickup' },
  { value: 'SAME_DAY', label: 'Same Day', hint: 'Terkirim hari yang sama' },
  { value: 'NEXT_DAY', label: 'Next Day', hint: 'Terkirim keesokan hari' },
  { value: 'REGULAR', label: 'Regular', hint: '2–3 hari kerja' },
] as const;

export type ManualShipmentServiceType =
  (typeof MANUAL_SHIPMENT_SERVICE_TYPES)[number]['value'];

// Package size → cubic-metre volume (mirrors next-portal's S/M box sizes; L
// extends the same 10cm cube step). The item model has no per-item volume
// column, so these sum into the AWB-level `Total Volume (m3)` at build time.
export const MANUAL_SHIPMENT_SIZES = [
  { value: 'S', dim: '40×40×40 cm', volume: 0.064 },
  { value: 'M', dim: '50×50×50 cm', volume: 0.125 },
  { value: 'L', dim: '60×60×60 cm', volume: 0.216 },
] as const;

export type ManualShipmentSize =
  (typeof MANUAL_SHIPMENT_SIZES)[number]['value'];

export const manualShipmentSizeVolume = (size?: string): number =>
  MANUAL_SHIPMENT_SIZES.find((s) => s.value === size)?.volume ?? 0;

export interface ManualShipmentPackage {
  name: string; // Item Name — required, unique across the shipment
  weightKg?: string; // Item weight (kg)
  size?: string; // S/M/L — contributes to the AWB Total Volume (m3)
  type?: string; // package type — folded into Item Description (no own column)
  description?: string; // Item Description
}

export interface ManualShipmentInput {
  bookingId?: string; // optional; blank → auto (waybill stands in) for 1 package
  receiverName: string; // required
  senderName?: string; // optional; backfilled from the hub when blank
  originPhone?: string;
  destinationAddress: string; // required
  destinationLat: number; // required (from the map picker)
  destinationLong: number; // required
  destinationPhone?: string;
  serviceType?: string; // default INSTANT
  packages: ManualShipmentPackage[]; // ≥1, each with a non-blank name
}

// Recognised import columns (subset of KNOWN_COLUMNS in the BE
// import-shipments.shared.ts). One row per package so each item keeps its own
// weight/description; every row shares one Booking ID so the parser groups them
// into a single shipment.
const MANUAL_SHIPMENT_COLUMNS = [
  'Booking ID',
  'Receiver Name',
  'Sender Name',
  'Origin Phone',
  'Destination Address',
  'Destination Lat',
  'Destination Long',
  'Destination Phone',
  'Service Type',
  'Total Volume (m3)',
  'Item Name',
  'Item weight (kg)',
  'Item Description',
] as const;

// Fold the optional package "type" into the (real) Item Description column,
// since there's no dedicated type/size column in the import contract.
const composeItemDescription = (pkg: ManualShipmentPackage): string => {
  const type = (pkg.type ?? '').trim();
  const desc = (pkg.description ?? '').trim();
  if (type && desc) return `${type} — ${desc}`;
  return type || desc;
};

// Turn the manual form into an in-memory CSV File, ready for the import
// dry-run / commit endpoints.
export const buildManualShipmentCsvFile = (input: ManualShipmentInput): File => {
  // Grouping multiple packages under one shipment needs a shared Booking ID.
  // Preserve the "blank → auto (= waybill)" behaviour for a lone package.
  const groupId =
    (input.bookingId ?? '').trim() ||
    (input.packages.length > 1
      ? `LINK-${Date.now().toString(36).toUpperCase()}`
      : '');
  const service = (input.serviceType ?? '').trim() || 'INSTANT';
  const lat = String(input.destinationLat);
  const long = String(input.destinationLong);

  // Total Volume is an AWB-level field the importer reads from the FIRST row of
  // the group, so write the same summed volume on every row (never per-package,
  // which would undercount to just the head package's size).
  const totalVolume = input.packages.reduce(
    (sum, p) => sum + manualShipmentSizeVolume(p.size),
    0
  );
  const totalVolumeCell = totalVolume > 0 ? totalVolume.toFixed(3) : '';

  const rows = input.packages.map((pkg) => [
    groupId,
    input.receiverName.trim(),
    (input.senderName ?? '').trim(),
    (input.originPhone ?? '').trim(),
    input.destinationAddress.trim(),
    lat,
    long,
    (input.destinationPhone ?? '').trim(),
    service,
    totalVolumeCell,
    pkg.name.trim(),
    (pkg.weightKg ?? '').trim(),
    composeItemDescription(pkg),
  ]);

  const BOM = '﻿';
  const lines = [Array.from(MANUAL_SHIPMENT_COLUMNS), ...rows].map((row) =>
    row.map(toCsvCell).join(',')
  );
  const content = BOM + lines.join('\r\n') + '\r\n';
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  return new File([blob], `manual-shipment-${Date.now()}.csv`, {
    type: 'text/csv',
  });
};

// Reads the JSON error message out of a blob response (errors come back as a
// Blob because the request sets responseType: 'blob' for the file download).
const parseExportBlobError = async (error: any): Promise<string> => {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      return parsed.message || parsed.error || getBackendErrorMessage(error);
    } catch {
      return getBackendErrorMessage(error);
    }
  }
  return getBackendErrorMessage(error);
};

// Server-side export: hit GET /v1/shipments/export with the selected filters
// and download the CSV file the backend streams back. The backend builds the
// CSV (one row per item, import-template columns); the client only triggers
// the download.
/** One selectable export layout, as listed for this client. */
export interface ExportTemplateSummary {
  key: string;
  /** EXIM direction the layout is built for; null for direction-agnostic ones. */
  exim: ShipmentExim | null;
}

interface ListExportTemplatesResponse {
  status: string;
  data: { templates: ExportTemplateSummary[] };
}

/**
 * Export layouts this client may pick in Custom export — global ones plus the
 * ones it owns (the gateway scopes by the signed-in client; another client's
 * templates are never returned).
 */
export const listExportTemplates = async (): Promise<ExportTemplateSummary[]> => {
  try {
    const response = await Axios.get<ListExportTemplatesResponse>(
      '/v1/export-templates',
      { headers: CHANNEL_HEADERS_LIVE }
    );
    return response.data.data.templates ?? [];
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

export const exportShipmentsCSV = async (
  params: Omit<ShipmentListParams, 'page' | 'limit'> & {
    /** Custom export: an explicit layout; omitted → the client's configured one. */
    templateKey?: string;
  }
): Promise<void> => {
  // Forward the same filter set the list endpoint accepts (minus pagination)
  // so the exported file matches the on-screen, filtered view.
  const query: Record<string, string | number> = {};
  if (params.status) query.status = params.status;
  // EXIM direction tag. The backend also uses it to pick the client's
  // directional template (e.g. Patimban + EXPORT → patimban-export).
  if (params.exim) query.exim = params.exim;
  // Custom export: an explicit layout wins over the client's mapping.
  if (params.templateKey) query.templateKey = params.templateKey;
  if (params.search) query.search = params.search;
  if (params.clientId) query.clientId = params.clientId;
  if (params.hubId) query.hubId = params.hubId;
  if (params.uploadShipmentCode)
    query.uploadShipmentCode = params.uploadShipmentCode;
  if (params.createdFrom) query.createdFrom = params.createdFrom;
  if (params.createdTo) query.createdTo = params.createdTo;

  try {
    const response = await Axios.get('/v1/shipments/export', {
      headers: CHANNEL_HEADERS_LIVE,
      params: query,
      responseType: 'blob',
    });

    // Prefer the filename the backend set via Content-Disposition.
    const disposition = response.headers['content-disposition'] as
      | string
      | undefined;
    const match = disposition?.match(/filename="?([^"]+)"?/i);
    const filename =
      match?.[1] || `shipments-${new Date().toISOString().slice(0, 10)}.csv`;

    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (error) {
    throw new Error(await parseExportBlobError(error));
  }
};


